import React, {useEffect, useRef, useState} from "react";
import { Badge, Modal, Form, Table, ButtonGroup, ToggleButton, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { Bell } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserNurse,
  faBriefcaseMedical,
  faRunning
} from "@fortawesome/free-solid-svg-icons";

import EditableExpires from "../../utils/EditableExpires";
import logo from "../../assets/img/logo.png";

const { REACT_APP_BACKEND_URL } = process.env;

const CarrierSelect = ({ token, selectedCarrierId, handleCarrierChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "")
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/carriers")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token])


  if (!isLoaded)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
    let carrierOptionList = [<option value={0} key={0}>Select insurance carrier...</option>];
    carrierOptionList.push(data.map((carrier, i) => {
      return (
        <option value={carrier.id} key={carrier.id} data-has-business-lines={carrier.hasBusinessLines} data-is-out-of-network={carrier.isOutOfNetwork} data-note={carrier.note}>{carrier.name}</option>
      )
    }, this));

    return (
      <Form.Select style={{height: "36px"}} onChange={(e) => {handleCarrierChanged(e)}} value={selectedCarrierId}>{carrierOptionList}</Form.Select>
    )
  }
}

const BusinessLineSelect = ({ token, carrierId, selectedBusinessLineId, handleBusinessLineChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "" && carrierId !== 0)
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/carrier/" + carrierId + "/businesslines")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token, carrierId])


  if (carrierId == null || carrierId === 0)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Please first select an insurance carrier...</option></Form.Select>);
  }
  else if (!isLoaded)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
    let businessLineOptionList = [<option value={0} key={0}>Select plan...</option>];
    businessLineOptionList.push(data.map((bl, i) => {
      return (
        <option value={bl.id} key={bl.id}>{bl.name}</option>
      )
    }, this));

    return (
      <Form.Select onChange={(e) => {handleBusinessLineChanged(e)}} value={selectedBusinessLineId}>{businessLineOptionList}</Form.Select>
    )
  }
}

function containsAllWords(words, name)
{
  for (var i=0; i < words.length; i++)
  {
    if(!name || name.toLowerCase().indexOf(words[i].trim().toLowerCase()) < 0)
    {
      return false;
    }
  }

  return true;
}

const CarrierAndBusinessLineSelect = ({ token, handleCarrierAndBusinessLineChanged, selectedCarrierAndBusinessLine}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "")
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/carriersandbusinesslines")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }

          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token])

  if (!isLoaded)
  {
    return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
      const options = data.map((plan) => {
          return {value: plan.carrierId + ":" + plan.businessLineId, label: plan.name, carrierId: plan.carrierId, businessLineId: plan.businessLineId, isOutOfNetwork: plan.isOutOfNetwork};
      });

      const customFilter = (option, searchText) => {
        let words = searchText.trim().split(" ");
        return containsAllWords(words, option.data.label);
      };

      return (
        <Select
        options={options}
        filterOption={customFilter}
        onChange={(selectedCarrierAndBusinessLine) => {
          handleCarrierAndBusinessLineChanged(selectedCarrierAndBusinessLine);
        }}
        value={selectedCarrierAndBusinessLine}
        placeholder="Search for insurance carrier or plan..."
        isClearable={true}
        backspaceRemovesValue={true}

        theme={(theme) => ({
          ...theme,
          colors: {
          ...theme.colors,
            primary50: '#DEF2F1',
            primary25: '#DEF2F1',
            primary: '#3AAFA9',
          },
        })}
        />
      )
  }
}


const ProviderList = ({ token, businessLineId, carrierId, carrierHasBusinessLines}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "" && (businessLineId > 0 || !carrierHasBusinessLines))
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/carrier/" + carrierId + "/businessline/" + businessLineId)
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token, businessLineId, carrierId, carrierHasBusinessLines])


  if (carrierHasBusinessLines && (businessLineId == null || businessLineId <= 0))
  {
    return (<span>Please first select an insurance carrier and plan...</span>);
  }
  else if (!isLoaded)
  {
    return (<span>Loading...</span>);
  }
  else if (error || !data)
  {
    return (<span>Error... Please reload the page...</span>);
  }
  else
  {
    let providerList = data.map((provider, i) => {
      let locationlist = provider.locations.map((location, i) => {
        return (
          <tr key={i}>
            <td>{location.name}</td>
            <td>{location.city}</td>
            <td>{location.state}</td>
            <td>{location.effectiveDate && <EditableExpires localExpires={location.effectiveDate} warnWithColors={false} localActive={true} editingExpires={false} />}</td>
          </tr>
        )
      }, this);

      var icon = faRunning;
      if (provider.isPhysician)
      {
        icon = faBriefcaseMedical;
      }
      else if (provider.isApp)
      {
        icon = faUserNurse
      }

      return (
        <tr key={i}>
          <td><FontAwesomeIcon icon={icon} style={{height: "16px", width: "16px", color: "#2B7A78"}} />&nbsp;&nbsp;<b>{provider.lastName}, {provider.firstName} {provider.credentials}</b></td>
          <td>
            <Table striped className="table-sm">
              <thead>
                <tr>
                  <td>Name</td>
                  <td>City</td>
                  <td>State</td>
                  <td>Effective Date</td>
                </tr>
              </thead>
              <tbody>
                {locationlist}
              </tbody>
            </Table>

          </td>
        </tr>
      )
    }, this);

    if (providerList.length === 0)
    {
      return (
        <span>No active providers found</span>
      );
    }

    return (
      <Table hover className="table-sm">
      <thead>
        <tr>
          <td><b>Provider</b></td>
          <td><b>Locations</b></td>
        </tr>
      </thead>
      <tbody>
        {providerList}   
      </tbody>
    </Table>
    );
  }
}

const DropdownNetworkLookup = ({token, onCarrierNoteChanged}) => {
  const[selectedCarrierId, setSelectedCarrierId] = useState(0);
  const[carrierHasBusinessLines, setCarrierHasBusinessLines] = useState(false);
  const[carrierIsOutOfNetwork, setCarrierIsOutOfNetwork] = useState(false);
  const[selectedBusinessLineId, setSelectedBusinessLineId] = useState(0);

  const handleCarrierChanged = (e) => {
    e.preventDefault();
    setSelectedCarrierId(parseInt(e.target.value));
    setCarrierHasBusinessLines(e.target.options[e.target.selectedIndex].getAttribute("data-has-business-lines") === "true");
    setCarrierIsOutOfNetwork(e.target.options[e.target.selectedIndex].getAttribute("data-is-out-of-network") === "true");
    onCarrierNoteChanged(e.target.options[e.target.selectedIndex].getAttribute("data-note"));
    setSelectedBusinessLineId(0);
  }

  const handleBusinessLineChanged = (e) => {
    e.preventDefault();
    setSelectedBusinessLineId(parseInt(e.target.value));
  }

  return (
    <>
      <Table className="table-borderless table-sm">
        <tbody>
          <tr>
            <td> <CarrierSelect token={token} selectedCarrierId={selectedCarrierId} handleCarrierChanged={handleCarrierChanged} /></td>
          </tr>
        {
           carrierIsOutOfNetwork && 
           <tr>
            <td colSpan={2}>
              <br />
              Please verify patient's out of network benefits
            </td>
          </tr>
        }
        { !carrierIsOutOfNetwork && selectedCarrierId !== 0 && carrierHasBusinessLines &&
          <tr>
            <td><BusinessLineSelect token={token} carrierId={selectedCarrierId} selectedBusinessLineId={selectedBusinessLineId} handleBusinessLineChanged={handleBusinessLineChanged} /></td>
          </tr>
        }
        { !carrierIsOutOfNetwork && selectedCarrierId !== 0 && !carrierHasBusinessLines &&
          <tr>
            <td colSpan={2}>
              <br />
              <ProviderList token={token} carrierId={selectedCarrierId} businessLineId={selectedBusinessLineId} carrierHasBusinessLines={carrierHasBusinessLines} />
            </td>
          </tr>
        }
        </tbody>
      </Table>
      <Table>
        <tbody>
        { selectedBusinessLineId > 0 &&
        <tr>
          <td colSpan={2}>
            <br />
            <ProviderList token={token} carrierId={selectedCarrierId} businessLineId={selectedBusinessLineId} carrierHasBusinessLines={carrierHasBusinessLines} />
          </td>
        </tr>
        }
        </tbody>
      </Table>
    </>

  );
}

const SearchNetworkLookup = ({token}) => {
  const[selectedCarrierAndBusinessLine, setSelectedCarrierAndBusinessLine] = useState("");
  const[selectedCarrierId, setSelectedCarrierId] = useState(0);
  const[selectedBusinessLineId, setSelectedBusinessLineId] = useState(0);
  const[carrierIsOutOfNetwork, setCarrierIsOutOfNetwork] = useState(false);

  const handleCarrierAndBusinessLineChanged = (selectedCarrierAndBusinessLine) => {
    if (selectedCarrierAndBusinessLine == null)
    {
      setSelectedCarrierAndBusinessLine("");
      setSelectedCarrierId(0);
      setSelectedBusinessLineId(0);
      setCarrierIsOutOfNetwork(false);
    }
    else
    {
      setSelectedCarrierAndBusinessLine(selectedCarrierAndBusinessLine);
      setSelectedCarrierId(parseInt(selectedCarrierAndBusinessLine.carrierId));
      setSelectedBusinessLineId(parseInt(selectedCarrierAndBusinessLine.businessLineId));
      setCarrierIsOutOfNetwork(selectedCarrierAndBusinessLine.isOutOfNetwork);
    }
  }

  return (
    <>
      <Table className="table-borderless table-sm">
        <tbody>
          <tr>
            <td> <CarrierAndBusinessLineSelect token={token} handleCarrierAndBusinessLineChanged={handleCarrierAndBusinessLineChanged} selectedCarrierAndBusinessLine={selectedCarrierAndBusinessLine} /></td>
          </tr>
        </tbody>
      </Table>
      <Table>
        <tbody>
        {
           carrierIsOutOfNetwork && selectedCarrierId > 0 &&
           <tr>
            <td colSpan={2}>
              <br />
              Please verify patient's out of network benefits
            </td>
          </tr>
        }
        { !carrierIsOutOfNetwork && selectedCarrierId > 0 &&
        <tr>
          <td colSpan={2}>
            <br />
            <ProviderList token={token} carrierId={selectedCarrierId} businessLineId={selectedBusinessLineId} carrierHasBusinessLines={selectedBusinessLineId > 0} />
          </td>
        </tr>
        }
        </tbody>
      </Table>
    </>

  );
}

const ProviderSelect = ({ token, selectedProviderId, handleProviderChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "")
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/providers")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token])


  if (!isLoaded)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
    let providerOptionList = [<option value={0} key={0}>Select a provider...</option>];
    providerOptionList.push(data.map((provider, i) => {
      return (
        <option value={provider.id} key={provider.id}>{provider.name}{provider.npi ? ` [${provider.npi}]` : ""}</option>
      )
    }, this));

    return (
      <Form.Select style={{height: "36px"}} onChange={(e) => {handleProviderChanged(e)}} value={selectedProviderId}>{providerOptionList}</Form.Select>
    )
  }
}

const ProviderSearchableSelect = ({ token, selectedProvider, handleProviderChanged}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "")
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/providers")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token])


  if (!isLoaded)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select style={{height: "36px"}} disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
    const options = data.map((provider) => {
      return {value: provider.id, label: provider.name + (provider.npi ? ` [${provider.npi}]` : "")};
    });

    const customFilter = (option, searchText) => {
      let words = searchText.trim().split(" ");
      return containsAllWords(words, option.data.label);
    };

    return (
      <Select
      options={options}
      filterOption={customFilter}
      onChange={(selectedProvider) => {
        handleProviderChanged(selectedProvider);
      }}
      value={selectedProvider}
      placeholder="Search for a provider..."
      isClearable={true}
      backspaceRemovesValue={true}

      theme={(theme) => ({
        ...theme,
        colors: {
        ...theme.colors,
          primary50: '#DEF2F1',
          primary25: '#DEF2F1',
          primary: '#3AAFA9',
        },
      })}
      />
    )
  }
}

const CarrierAndBusinessLineList = ({ token, providerId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (token != null && token !== "" && providerId > 0)
    {
      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/provider/" + providerId)
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token, providerId])


  if (providerId <= 0)
  {
    return (<span>Please first select a provider...</span>);
  }
  else if (!isLoaded)
  {
    return (<span>Loading...</span>);
  }
  else if (error || !data)
  {
    return (<span>Error... Please reload the page...</span>);
  }
  else
  {
    let carrierAndBusinessLineList = data.map((carrierAndBusinessLine, i) => {
      let locationlist = carrierAndBusinessLine.locations.map((location, i) => {
        return (
          <tr key={i}>
            <td>{location.name}</td>
            <td>{location.city}</td>
            <td>{location.state}</td>
            <td>{location.effectiveDate && <EditableExpires localExpires={location.effectiveDate} warnWithColors={false} localActive={true} editingExpires={false} />}</td>
          </tr>
        )
      }, this);

      return (
        <tr key={i}>
          <td><b>{carrierAndBusinessLine.name}</b></td>
          <td>
            <Table striped className="table-sm">
              <thead>
                <tr>
                  <td>Name</td>
                  <td>City</td>
                  <td>State</td>
                  <td>Effective Date</td>
                </tr>
              </thead>
              <tbody>
                {locationlist}
              </tbody>
            </Table>

          </td>
        </tr>
      )
    }, this);

    if (carrierAndBusinessLineList.length === 0)
    {
      return (
        <span>No active carriers found</span>
      );
    }

    return (
      <Table hover className="table-sm">
      <thead>
        <tr>
          <td style={{width: "40%"}}><b>Carrier</b></td>
          <td><b>Locations</b></td>
        </tr>
      </thead>
      <tbody>
        {carrierAndBusinessLineList}   
      </tbody>
    </Table>
    );
  }
}

const DropdownProviderLookup = ({token, onProviderIdChanged}) => {
  const[selectedProviderId, setSelectedProviderId] = useState(0);

  const handleProviderChanged = (e) => {
    e.preventDefault();
    setSelectedProviderId(parseInt(e.target.value));
  }

  // Reset on initial and handle any change
  useEffect(() => {
    onProviderIdChanged(selectedProviderId);
  }, [selectedProviderId]);

  return (
    <>
      <Table className="table-borderless table-sm">
        <tbody>
          <tr>
            <td> <ProviderSelect token={token} selectedProviderId={selectedProviderId} handleProviderChanged={handleProviderChanged} /></td>
          </tr>

        { selectedProviderId !== 0 &&
          <tr>
            <td colSpan={2}>
              <br />
              <CarrierAndBusinessLineList token={token} providerId={selectedProviderId} />
            </td>
          </tr>
        }
        </tbody>
      </Table>
    </>
  );
}

const SearchProviderLookup = ({token, onProviderIdChanged}) => {
  const[selectedProvider, setSelectedProvider] = useState("");
  const[selectedProviderId, setSelectedProviderId] = useState(0);

  const handleProviderChanged = (selectedProvider) => {
    if (selectedProvider)
    {
      setSelectedProvider(selectedProvider);
      setSelectedProviderId(selectedProvider.value);
    }
    else
    {
      setSelectedProvider("");
      setSelectedProviderId(0);
    }
  }

  // Reset on initial and handle any change
  useEffect(() => {
    onProviderIdChanged(selectedProviderId);
  }, [selectedProviderId]);

  return (
    <>
      <Table className="table-borderless table-sm">
        <tbody>
          <tr>
            <td> <ProviderSearchableSelect token={token} selectedProvider={selectedProvider} handleProviderChanged={handleProviderChanged} /></td>
          </tr>

        { selectedProviderId !== 0 &&
          <tr>
            <td colSpan={2}>
              <br />
              <CarrierAndBusinessLineList token={token} providerId={selectedProviderId} />
            </td>
          </tr>
        }
        </tbody>
      </Table>
    </>
  );
}

const NewEnrollmentsList = ({ data }) => {
  var enrollmentList = data.map((enrollment, i) => {
    return (
      <tr key={i}>
        <td>{enrollment.lastName}, {enrollment.firstName} {enrollment.credentials}</td>
        <td>{enrollment.carrier}</td>
        <td><strong>{enrollment.effectiveDate && <EditableExpires localExpires={enrollment.effectiveDate} warnWithColors={false} localActive={true} editingExpires={false} />}</strong></td>
        <td>{enrollment.approvalDate && new Date(enrollment.approvalDate).toLocaleDateString("en-US")}</td>
      </tr>
    )
  }, this);

  return (
    <Table striped hover style={{fontSize: "0.75rem", borderColor: "#adb5bd"}} className="table-sm">
      <thead>
        <tr>
          <th>Provider</th>
          <th>Carrier</th>
          <th>Effective Date</th>
          <th>Approval Date</th>
        </tr>
      </thead>
      <tbody>
        {enrollmentList}
      </tbody>
    </Table>
  )
}

const NewEnrollments = ({ token, selectedProviderId }) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const latestRequestId = useRef(0);
  const inIframe = () => window.self !== window.top;

  useEffect(() => {
    // Handle out of order responses by only applying the most recent request
    const requestId = ++latestRequestId.current;

    if (token != null && token !== "" && !inIframe())
    {
      setError(null);
      setIsLoaded(false);
      setData([]);
      
      const providerIdParam = selectedProviderId > 0 ? "?providerId=" + selectedProviderId : "";

      fetch(REACT_APP_BACKEND_URL + "network/practice/" + token + "/newenrollments" + providerIdParam)
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }

          throw Error(res.status);
      })
      .then(
        (result) => {
          if (requestId !== latestRequestId.current)
          {
            return;
          }

          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          if (requestId !== latestRequestId.current)
          {
            return;
          }

          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setError(null);
      setIsLoaded(false);
      setData([]);
    }
  }, [token, selectedProviderId])

  if (inIframe() || !isLoaded || error || !data || data.length === 0)
  {
    return null;
  }

  return (
    <>
      <Badge
        pill
        bg="warning"
        className="d-inline-flex align-items-center"
        style={{cursor: "pointer", opacity: 0.9, fontSize: "0.72rem", padding: "0.35rem 0.6rem", gap: "0.25rem"}}
        onClick={() => {setShowModal(true)}}
      >
        <Bell size={12} />
        {data.length} new {selectedProviderId > 0 ? "for " + data[0].lastName + ", " + data[0].firstName + " " + data[0].credentials : ""}
      </Badge>

      <Modal show={showModal} onHide={() => {setShowModal(false)}}>
        <Modal.Header closeButton closeVariant="white" style={{backgroundColor: "#2B7A78"}}>
          <Modal.Title className="text-white fw-bold text-uppercase d-flex align-items-center">
            <img alt="H3" src={logo} width={18} height={18} />
            &nbsp;&nbsp;New Enrollments
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{maxHeight: "600px", overflowY: "auto"}}>
          <NewEnrollmentsList data={data} />
        </Modal.Body>
      </Modal>
    </>
  );
}

const NetworkBySelect = ({ selectedNetworkBy, handleNetworkByChanged}) => {
  return (
      <ButtonGroup className="me-auto">
          <ToggleButton
              key="CARRIER"
              id="CARRIER"
              type="radio"
              variant="outline-primary"
              style={{color: selectedNetworkBy === "CARRIER" ? "white" : ""}}
              value="CARRIER"
              checked={selectedNetworkBy === "CARRIER"}
              onChange={(e) => {handleNetworkByChanged(e.target.value)}}
          >By Carrier</ToggleButton>
          <ToggleButton
              key="PROVIDER"
              id="PROVIDER"
              type="radio"
              variant="outline-primary"
              style={{color: selectedNetworkBy === "PROVIDER" ? "white" : ""}}
              value="PROVIDER"
              checked={selectedNetworkBy === "PROVIDER"}
              onChange={(e) => {handleNetworkByChanged(e.target.value)}}
          >By Provider</ToggleButton>
      </ButtonGroup>   
  )
}

const NetworkLookup = ({by, token}) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [carrierNote, setCarrierNote] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState(0);
  const inIframe = () => window.self !== window.top;

  const handleNetworkByChanged = (networkBy) => {
    setLoading(true);

    if (searchParams.toString() !== "")
    {
      window.location = "/network/" + networkBy.toLowerCase() + "/" + token + "?" + searchParams.toString();
    }
    else
    {
      window.location = "/network/" + networkBy.toLowerCase() + "/" + token;
    }
  }

  const onProviderIdChanged = (providerId) => {
      setSelectedProviderId(providerId);
  }

  if (searchParams.get("version") === "search")
  {
    return (
    <>
        <div className="d-flex justify-content-between align-items-center" style={{marginTop: "-15px"}}>
          <div><NewEnrollments token={token} selectedProviderId={selectedProviderId} /></div>
          <div>{loading ? <Spinner size="sm" animation="border" style={{marginRight: "70px", marginTop: "5px"}} /> : <NetworkBySelect selectedNetworkBy={by.toUpperCase()} handleNetworkByChanged={handleNetworkByChanged}/>}</div>
        </div>
        <br /><br />
        {by === "provider" ? <SearchProviderLookup token={token} onProviderIdChanged={onProviderIdChanged} /> : <SearchNetworkLookup token={token} /> }
    </>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center" style={{marginTop: "-15px"}}>
        <div className="d-flex align-items-center" style={{gap: "0.75rem"}}>
          <NewEnrollments token={token} selectedProviderId={selectedProviderId} />
          { !inIframe() && carrierNote &&
            <div style={{backgroundColor: "#f1f3f5", color: "#495057", borderRadius: "16px", padding: "0.4rem 0.9rem", fontSize: "0.85rem", maxWidth: "420px"}}>
              {carrierNote}
            </div>
          }
        </div>
        <div>{loading ? <Spinner size="sm" animation="border" style={{marginRight: "70px", marginTop: "5px"}} /> : <NetworkBySelect selectedNetworkBy={by.toUpperCase()} handleNetworkByChanged={handleNetworkByChanged}/>}</div>
      </div>
      <br /><br />
      {by === "provider" ? <DropdownProviderLookup token={token} onProviderIdChanged={onProviderIdChanged} /> : <DropdownNetworkLookup token={token} onCarrierNoteChanged={setCarrierNote} /> }
    </>
  );
};


export default NetworkLookup;
